import {inject, injectable} from "inversify";
import {computed, makeAutoObservable, observable, runInAction} from "mobx";
import {Bindings} from "data/constants/bindings";
import type {IJSONProvider} from "data/providers/json/json.provider";
import {Language} from "data/enums";
import i18n from "i18next";

export type IHelpPages = Record<string, IHelpPage[]>;

export interface IHelpPage {
	id: number;
	type?: string;
	title: string;
	text: string;
	game: string;
}

export interface IStaticContentPayload {
	locale: string;
	per_page: number;
	page?: number;
	categoryId?: number;
}

export interface IStaticContent {
	page: number;
	previous_page: null;
	next_page: null;
	per_page: number;
	page_count: number;
	count: number;
	sort_by: string;
	sort_order: string;
}

export interface ISections extends IStaticContent {
	sections: {
		id: number;
		url: string;
		html_url: string;
		category_id: number;
		position: number;
		sorting: string;
		created_at: string;
		updated_at: string;
		name: string;
		description: string;
		locale: string;
		source_locale: string;
		outdated: boolean;
		parent_section_id: null;
		theme_template: string;
	}[];
}

export interface ICategories extends IStaticContent {
	categories: {
		id: number;
		url: string;
		html_url: string;
		position: number;
		created_at: string;
		updated_at: string;
		name: string;
		description: string;
		locale: string;
		source_locale: string;
		outdated: boolean;
	}[];
}

export interface IArticles extends IStaticContent {
	articles: {
		id: number;
		url: string;
		html_url: string;
		author_id: number;
		comments_disabled: boolean;
		draft: boolean;
		promoted: boolean;
		position: number;
		vote_sum: number;
		vote_count: number;
		section_id: number;
		created_at: string;
		updated_at: string;
		name: string;
		title: string;
		source_locale: string;
		locale: string;
		outdated: boolean;
		outdated_locales: unknown[];
		edited_at: string;
		user_segment_id: null;
		permission_group_id: number;
		label_names: string[];
		body: string;
	}[];
}

export type TArticle = IArticles["articles"][number];

export interface IStaticContentStore {
	get sections(): ISections["sections"];
	get articles(): IArticles["articles"];
	get prizes(): TArticle | undefined;

	fetchStaticContent(): Promise<void>;
}

@injectable()
export class StaticContentStore implements IStaticContentStore {
	@observable private _categories?: ICategories;
	@observable private _articles?: IArticles;
	@observable private _sections?: ISections;

	@computed get sections() {
		return this._sections?.sections ?? [];
	}

	@computed get articles() {
		return this._articles?.articles ?? [];
	}

	@computed get prizes() {
		return this.articles.find((it) => ["Prizing", "Prix"].includes(it.name));
	}

	constructor(@inject(Bindings.JSONProvider) private readonly _jsonProvider: IJSONProvider) {
		makeAutoObservable(this);
	}

	public async fetchStaticContent() {
		await this._fetchCategory();
		await this._fetchSectionsAndArticles();
	}

	private async _fetchCategory() {
		const locale = this.lang;
		const categories = await this._jsonProvider.helpCategories({locale, per_page: 100});
		if (categories.data.categories.length) {
			runInAction(() => {
				this._categories = categories.data;
			});
		}
	}

	private async _fetchSectionsAndArticles() {
		const locale = this.lang;
		const categoryId = this.projectCategoryID;
		const [sections, articles] = await Promise.all([
			this._jsonProvider.helpSections({locale, per_page: 100, categoryId: categoryId}),
			this._jsonProvider.helpArticles({locale, per_page: 100, categoryId: categoryId}),
		]);

		runInAction(() => {
			this._articles = articles.data;
			this._sections = sections.data;
		});
	}

	private get projectCategoryID() {
		const projectCategory = this._categories?.categories.find(({description}) => {
			return description === `pickem`;
		});
		return projectCategory?.id;
	}

	private get lang() {
		return this.getHelpLang(i18n.language.toLowerCase() as Language);
	}

	getHelpLang = (lang: Language) => {
		return (
			{
				[Language.French]: "fr-ca",
				[Language.English]: "en-us",
			}[lang] || "en-us"
		);
	};
}
